import { useState, useMemo } from "react";
import { Trash2, AlertTriangle, X, Calculator, BookOpen } from "lucide-react";
import useDeleteApi from "../../hooks/delete_api_hook/useDeleteApi";
import { url } from "../../utils/confserveur";

const DeleteAnteriorEvalNat = ({ anteriorEvaluations, handleReload }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedEval, setSelectedEval] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { deleteData } = useDeleteApi();

  // Grouper par année (session) puis par niveau
  const groupedByYear = useMemo(() => {
    // Filtrer toutes les évaluations antérieures (classe_nom === "Non défini")
    const allAnteriorEvaluations = anteriorEvaluations?.filter(
      (evalNat) => evalNat.classe_nom === "Non défini"
    ) || [];

    const grouped = {};

    allAnteriorEvaluations.forEach((evalNat) => {
      const year = evalNat.session;
      const niveau = evalNat.stats?.niveau || "Inconnu";

      if (!grouped[year]) {
        grouped[year] = {};
      }
      if (!grouped[year][niveau]) {
        grouped[year][niveau] = { maths: null, français: null };
      }

      if (evalNat.matiere === 'maths') {
        grouped[year][niveau].maths = evalNat;
      } else if (evalNat.matiere === 'français') {
        grouped[year][niveau].français = evalNat;
      }
    });

    // Trier les années par ordre décroissant
    return Object.entries(grouped)
      .sort(([a], [b]) => b - a)
      .map(([year, niveaux]) => ({
        year: parseInt(year),
        niveaux: Object.entries(niveaux)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([niveau, matieres]) => ({
            niveau,
            maths: matieres.maths,
            français: matieres.français
          }))
      }));
  }, [anteriorEvaluations]);

  const handleDeleteClick = (evalNat) => {
    setSelectedEval(evalNat);
  };

  const handleConfirmDelete = async () => {
    if (!selectedEval) return;

    setIsDeleting(true);
    try {
      const endpoint = `${url}evaluations_nat/${selectedEval.id}/`;
      await deleteData(endpoint);

      alert(`Évaluation ${selectedEval.matiere === 'maths' ? 'Mathématiques' : 'Français'} ${selectedEval.stats?.niveau} - ${selectedEval.session} supprimée avec succès !`);
      handleReload();
      setSelectedEval(null);
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      alert("Une erreur s'est produite : " + (error.message || "Veuillez réessayer."));
    } finally {
      setIsDeleting(false);
    }
  };

  // Ne pas afficher le bouton s'il n'y a pas d'évaluations antérieures
  if (groupedByYear.length === 0) {
    return null;
  }

  return (
    <>
      {/* Bouton pour ouvrir la liste */}
      <button
        onClick={() => setShowModal(true)}
        className="inline-flex items-center gap-2 px-4 py-2.5 
        bg-gradient-to-r from-orange-500 to-orange-600 text-white
                rounded-xl font-semibold transition-all duration-300
                hover:from-orange-600 hover:to-orange-700 hover:-translate-y-0.5 xl:text-lg text-sm
                shadow-lg shadow-orange-500/30 hover:shadow-xl
                 hover:shadow-orange-500/50" 
      >
        <Trash2 size={18} />
        Gérer années antérieures
      </button>

      {/* Modal de sélection/confirmation */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => {
              setShowModal(false);
              setSelectedEval(null);
            }}
          />

          {/* Modal content */}
          <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="relative overflow-hidden bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-8 flex-shrink-0">
              {/* Bouton fermer */}
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedEval(null);
                }}
                className="absolute top-4 right-4 z-20 p-2 bg-white/20 backdrop-blur-sm rounded-xl text-white hover:bg-white/30 transition-colors"
              >
                <X size={20} />
              </button>

              {/* Blobs animés */}
              <svg className="absolute top-0 left-0 w-full h-full opacity-30" viewBox="0 0 200 200">
                <circle cx="20" cy="20" r="40" fill="#fdba74">
                  <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
                </circle>
                <circle cx="180" cy="80" r="30" fill="#fed7aa">
                  <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
                </circle>
              </svg>

              <div className="relative z-10 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                    <Trash2 className="text-white" size={32} />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Gérer les années antérieures
                </h2>
                <p className="text-orange-100">
                  Sélectionnez une évaluation à supprimer
                </p>
              </div>
            </div>

            {/* Contenu scrollable */}
            <div className="p-6 overflow-y-auto flex-1">
              {!selectedEval ? (
                <div className="space-y-6">
                  {groupedByYear.map(({ year, niveaux }) => (
                    <div key={year} className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-4 border border-slate-200">
                      {/* Année */}
                      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
                        <span className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl
                          flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-orange-500/30">
                          {year}
                        </span>
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">Session {year}</h3>
                          <p className="text-sm text-slate-500">{niveaux.length} niveau(x) importé(s)</p>
                        </div>
                      </div>

                      {/* Niveaux */}
                      <div className="space-y-3">
                        {niveaux.map(({ niveau, maths, français }) => (
                          <div key={niveau} className="bg-white rounded-xl p-3 border border-slate-200">
                            <p className="text-sm font-semibold text-slate-700 mb-2">{niveau}</p>
                            <div className="flex flex-wrap gap-2">
                              {/* Maths */}
                              {maths && (
                                <button
                                  onClick={() => handleDeleteClick(maths)}
                                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-cyan-50
                                    border border-blue-200 rounded-lg hover:from-red-50 hover:to-rose-50 hover:border-red-300
                                    transition-all duration-200 group"
                                >
                                  <Calculator size={16} className="text-blue-600 group-hover:text-red-500" />
                                  <span className="text-sm font-medium text-slate-700 group-hover:text-red-600">Mathématiques</span>
                                  <Trash2 size={14} className="text-slate-400 group-hover:text-red-500 ml-1" />
                                </button>
                              )}
                              {/* Français */}
                              {français && (
                                <button
                                  onClick={() => handleDeleteClick(français)}
                                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-50 to-pink-50
                                    border border-purple-200 rounded-lg hover:from-red-50 hover:to-rose-50 hover:border-red-300
                                    transition-all duration-200 group"
                                >
                                  <BookOpen size={16} className="text-purple-600 group-hover:text-red-500" />
                                  <span className="text-sm font-medium text-slate-700 group-hover:text-red-600">Français</span>
                                  <Trash2 size={14} className="text-slate-400 group-hover:text-red-500 ml-1" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Confirmation de suppression */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                      <p className="text-amber-800 text-sm">
                        Supprimer <strong>{selectedEval.matiere === 'maths' ? 'Maths' : 'Français'} {selectedEval.stats?.niveau} - {selectedEval.session}</strong> ?
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedEval(null)}
                      className="flex-1 py-3 px-4 bg-slate-100 text-slate-700 font-semibold rounded-xl
                        hover:bg-slate-200 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleConfirmDelete}
                      disabled={isDeleting}
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-xl
                        shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40
                        transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                        flex items-center justify-center gap-2"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Suppression...
                        </>
                      ) : (
                        <>
                          <Trash2 size={18} />
                          Supprimer
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteAnteriorEvalNat;
